import { setVentoraFanConfig } from 'src/services/ventora_fan';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
import { getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { real_id } from 'src/helpers/interface_helpers';
import type { Device } from 'src/types/Device';
import type {
	VentoraBoostConfig,
	VentoraFanConfig,
	VentoraHumConfig,
	VentoraIdleRefreshConfig,
	VentoraInMode,
	VentoraLightConfig,
	VentoraNightModeConfig,
	VentoraTempConfig,
} from 'src/types/ventora_fan';

export const VENTORA_FAN_COMPONENT_KEY = 'fan:0';

export const ventoraInModeOptions: VentoraInMode[] = [
	'detached',
	'follow',
	'auto',
	'force',
	'boost',
	'light',
];

export const defaultVentoraFanConfig: VentoraFanConfig = {
	id: 0,
	name: null,
	in_mode: 'auto',
	night_mode: {
		enable: false,
		limit: 40,
		active_between: ['22:00', '06:00'],
	},
	idle_refresh: {
		idle_speed: 0,
		enable: false,
		speed: 40,
		interval: 900,
		duration: 60,
	},
	light: {
		enable: false,
		speed: 60,
		on_delay: 0,
		off_delay: 60,
	},
	temp: {
		enable: false,
		speed: 60,
		thr: 26,
		off_delay: 60,
	},
	hum: {
		enable: false,
		speed: 80,
		thr: 60,
		off_delay: 60,
	},
	boost: {
		speed: 100,
		duration: 300,
	},
};

export const resolveVentoraFanConfig = (
	raw: Partial<VentoraFanConfig> | null | undefined,
): VentoraFanConfig => {
	const r = raw || {};
	return {
		...defaultVentoraFanConfig,
		...r,
		night_mode: resolveVentoraNightModeConfig(r.night_mode),
		idle_refresh: { ...defaultVentoraFanConfig.idle_refresh, ...(r.idle_refresh || {}) },
		light: { ...defaultVentoraFanConfig.light, ...(r.light || {}) },
		temp: { ...defaultVentoraFanConfig.temp, ...(r.temp || {}) },
		hum: { ...defaultVentoraFanConfig.hum, ...(r.hum || {}) },
		boost: { ...defaultVentoraFanConfig.boost, ...(r.boost || {}) },
	};
};

export const ventoraNightModeLimits: Record<
	keyof Pick<VentoraNightModeConfig, 'limit'>,
	[number, number]
> = {
	limit: [1, 100],
};

/** Normalizes `HH:mm` (or `H:mm`) for Fan night_mode `active_between`. */
export const normalizeVentoraTimeValue = (value: string | undefined, fallback: string): string => {
	if (!value || typeof value !== 'string') return fallback;
	const parts = value.trim().split(':');
	if (parts.length < 2) return fallback;
	const hours = Number(parts[0]);
	const minutes = Number(parts[1]);
	if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallback;
	const clampedHours = ventoraClamp(Math.trunc(hours), 0, 23);
	const clampedMinutes = ventoraClamp(Math.trunc(minutes), 0, 59);
	return `${String(clampedHours).padStart(2, '0')}:${String(clampedMinutes).padStart(2, '0')}`;
};

export const resolveVentoraActiveBetween = (
	raw: unknown,
	fallback: [string, string] = defaultVentoraFanConfig.night_mode.active_between,
): [string, string] => {
	if (!Array.isArray(raw) || raw.length < 2) return [...fallback];
	return [
		normalizeVentoraTimeValue(String(raw[0]), fallback[0]),
		normalizeVentoraTimeValue(String(raw[1]), fallback[1]),
	];
};

export const resolveVentoraNightModeConfig = (
	raw: Partial<VentoraNightModeConfig> | null | undefined,
): VentoraNightModeConfig => {
	const base = defaultVentoraFanConfig.night_mode;
	const r = raw || {};
	return {
		...base,
		...r,
		active_between: resolveVentoraActiveBetween(r.active_between, base.active_between),
		limit: ventoraClamp(ventoraToNumber(r.limit, base.limit), ...ventoraNightModeLimits.limit),
	};
};

export const ventoraIdleRefreshLimits: Record<keyof VentoraIdleRefreshConfig, [number, number]> = {
	idle_speed: [0, 40],
	enable: [0, 1],
	speed: [1, 100],
	interval: [300, 604800],
	duration: [30, 1800],
};

export const ventoraLightLimits: Record<keyof VentoraLightConfig, [number, number]> = {
	enable: [0, 1],
	speed: [40, 100],
	on_delay: [0, 300],
	off_delay: [0, 1800],
};

export const ventoraTempLimits: Record<keyof VentoraTempConfig, [number, number]> = {
	enable: [0, 1],
	speed: [1, 100],
	thr: [0, 70],
	off_delay: [0, 1800],
};

export const ventoraHumLimits: Record<keyof VentoraHumConfig, [number, number]> = {
	enable: [0, 1],
	speed: [1, 100],
	thr: [10, 90],
	off_delay: [0, 1800],
};

export const ventoraBoostLimits: Record<keyof VentoraBoostConfig, [number, number]> = {
	speed: [40, 100],
	duration: [30, 1800],
};

export const ventoraClamp = (value: number, min: number, max: number) =>
	Math.min(max, Math.max(min, value));

export const ventoraNumericValidation = (value: number, min: number, max: number) =>
	getErrorOrInfoMessageForMinAndMax(value, min, max);

export const ventoraHasValidationErrors = (validations: { error: string }[]): boolean =>
	validations.some((entry) => Boolean(entry.error));

export const ventoraToNumber = (value: unknown, fallback: number) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
};

/** Parses a text field value; empty or non-finite input yields `undefined` (do not overwrite draft). */
export const ventoraFiniteNumberFromInput = (raw: string): number | undefined => {
	const trimmed = raw.trim();
	if (trimmed === '') return undefined;
	const parsed = Number(trimmed);
	return Number.isFinite(parsed) ? parsed : undefined;
};

/** Gen2 cloud wraps RPC result in `data`; local `/rpc` merges `result` at the top level. */
const unwrapGen2RpcPayload = (res: Record<string, unknown>): Record<string, unknown> => {
	const d = res?.data;
	if (d !== undefined && d !== null && typeof d === 'object' && !Array.isArray(d)) {
		return d as Record<string, unknown>;
	}
	return res;
};

/** Runtime on/off from `fan:0` status (`on`); `turn` is only used in Fan.Set requests. */
export const readVentoraFanOn = (fan0: Record<string, unknown> | undefined): boolean => {
	if (!fan0) return false;
	return Boolean(fan0.on ?? fan0.turn);
};

/** Runtime % for UI: `speed` and `position` are both 1–100 on this device. */
export const readVentoraFanSpeedPercent = (fan0: Record<string, unknown> | undefined): number => {
	if (!fan0) return 0;
	if (typeof fan0.speed === 'number') {
		return ventoraClamp(fan0.speed, 1, 100);
	}
	if (typeof fan0.position === 'number') {
		return ventoraClamp(fan0.position, 1, 100);
	}
	return 0;
};

/** True when fan boost is running — FW may use `boost.active`, `running`, or `started_at`+`duration`. */
export const readVentoraFanBoostActive = (fan0: Record<string, unknown> | undefined): boolean => {
	if (!fan0 || typeof fan0.boost !== 'object' || fan0.boost === null) return false;
	const b = fan0.boost as Record<string, unknown>;
	if (typeof b.active === 'boolean') return b.active;
	if (typeof b.active === 'number') return b.active !== 0;
	if (typeof b.running === 'boolean') return b.running;
	if (typeof b.enable === 'boolean') return b.enable;
	if (typeof b.enabled === 'boolean') return b.enabled;
	const started =
		typeof b.started_at === 'number'
			? b.started_at
			: typeof b.started_at === 'string'
				? Number(b.started_at)
				: NaN;
	const duration =
		typeof b.duration === 'number'
			? b.duration
			: typeof b.duration === 'string'
				? Number(b.duration)
				: NaN;
	if (Number.isFinite(started) && started > 0 && Number.isFinite(duration) && duration > 0) {
		return Date.now() / 1000 < started + duration;
	}
	if (Number.isFinite(started) && started > 0) return true;
	return false;
};

export const readVentoraFanBoostTiming = (
	boost: Record<string, unknown> | undefined,
): { startedAt: number; duration: number } | undefined => {
	if (!boost) return undefined;
	const started =
		typeof boost.started_at === 'number'
			? boost.started_at
			: typeof boost.started_at === 'string'
				? Number(boost.started_at)
				: NaN;
	const duration =
		typeof boost.duration === 'number'
			? boost.duration
			: typeof boost.duration === 'string'
				? Number(boost.duration)
				: NaN;
	if (!Number.isFinite(started) || started <= 0 || !Number.isFinite(duration) || duration <= 0) {
		return undefined;
	}
	return { startedAt: started, duration };
};

/** Compact countdown for status pills, e.g. `14m 50s`. */
export const formatVentoraBoostTimeRemaining = (
	boost: Record<string, unknown> | undefined,
): string | undefined => {
	const timing = readVentoraFanBoostTiming(boost);
	if (!timing) return undefined;
	const remainingSec = Math.max(0, Math.ceil(timing.startedAt + timing.duration - Date.now() / 1000));
	if (remainingSec <= 0) return undefined;
	const minutes = Math.floor(remainingSec / 60);
	const seconds = remainingSec % 60;
	if (minutes > 0 && seconds > 0) return `${minutes}m ${seconds}s`;
	if (minutes > 0) return `${minutes}m`;
	return `${seconds}s`;
};

/** i18n key for `fan.source` when it matches a known Ventora input mode. */
export const formatVentoraFanSourceI18nKey = (source: unknown): string | undefined => {
	const normalized = String(source ?? '')
		.trim()
		.toLowerCase();
	if (!normalized) return undefined;
	if (ventoraInModeOptions.includes(normalized as VentoraInMode)) {
		return `ventora_in_mode_${normalized}`;
	}
	return undefined;
};

export const patchFan0StatusFromFanSetResponse = (device: Device, response: Record<string, unknown>) => {
	if (!response?.isok) return;
	const payload = unwrapGen2RpcPayload(response);
	const status = devices_status.getByRealId(device.id);
	if (!status?.['fan:0']) return;
	const patch: Record<string, unknown> = {};
	if (typeof payload.mode === 'string') patch.mode = payload.mode;
	if (typeof payload.turn === 'boolean') patch.on = payload.turn;
	if (typeof payload.on === 'boolean') patch.on = payload.on;
	if (typeof payload.speed === 'number') {
		patch.speed = ventoraClamp(payload.speed, 1, 100);
	} else if (typeof payload.position === 'number') {
		patch.speed = ventoraClamp(payload.position, 1, 100);
	}
	if (Object.keys(patch).length === 0) return;
	const id = real_id(device.id);
	devices_status.update_device_status({
		id,
		msg: { ...status, 'fan:0': { ...status['fan:0'], ...patch } },
	});
};

export const applyVentoraConfigPatchLocal = (
	device: Device,
	patch: Partial<VentoraFanConfig>,
	rpc?: Record<string, unknown>,
) => {
	const id = real_id(device.id);
	const currentSettings = devices_settings.getByRealId(device.id) || {};
	const currentConfig = resolveVentoraFanConfig(
		currentSettings?.[VENTORA_FAN_COMPONENT_KEY] as Partial<VentoraFanConfig> | undefined,
	);
	const inModeFromRpc =
		rpc && typeof rpc.in_mode === 'string' ? (rpc.in_mode as VentoraInMode) : undefined;
	const nextConfig = {
		...currentConfig,
		...patch,
		...(inModeFromRpc !== undefined ? { in_mode: inModeFromRpc } : {}),
	};
	if (patch.night_mode) {
		nextConfig.night_mode = resolveVentoraNightModeConfig({
			...currentConfig.night_mode,
			...patch.night_mode,
		});
	}
	if (patch.idle_refresh) {
		nextConfig.idle_refresh = { ...currentConfig.idle_refresh, ...patch.idle_refresh };
	}
	if (patch.light) {
		nextConfig.light = { ...currentConfig.light, ...patch.light };
	}
	if (patch.temp) {
		nextConfig.temp = { ...currentConfig.temp, ...patch.temp };
	}
	if (patch.hum) {
		nextConfig.hum = { ...currentConfig.hum, ...patch.hum };
	}
	if (patch.boost) {
		nextConfig.boost = { ...currentConfig.boost, ...patch.boost };
	}
	devices_settings.update_devices_settings(id, {
		...currentSettings,
		[VENTORA_FAN_COMPONENT_KEY]: nextConfig,
	});
};

export const patchVentoraFanConfig = async (
	device: Device,
	patch: Partial<VentoraFanConfig>,
): Promise<boolean> => {
	const res = await setVentoraFanConfig(device, patch);
	const response = onSettingsResponse(res, device);
	if (response?.isok) {
		applyVentoraConfigPatchLocal(device, patch, response as unknown as Record<string, unknown>);
		return true;
	}
	return false;
};

export const patchVentoraInputType = async (
	device: Device,
	type: 'button' | 'switch',
): Promise<boolean> => {
	const res = await gen2Request(device, 'Input.SetConfig', {
		id: device.channel,
		config: { type },
	});
	const response = onSettingsResponse(res, device);
	if (!response?.isok) return false;
	const rid = real_id(device.id);
	const settings = devices_settings.getByRealId(device.id);
	if (!settings) return true;
	const inputKey = `input:${device.channel}`;
	const prev = settings[inputKey];
	if (!prev || typeof prev !== 'object') return true;
	devices_settings.update_devices_settings(rid, {
		...settings,
		[inputKey]: { ...prev, type },
	});
	return true;
};
